import React, { ReactNode } from 'react';
import { Alert, Spin } from 'antd';
import { ServiceName, useServiceStatus } from '@/contexts/service-status.context';
import { ServiceUnavailable } from '../ServiceUnavailable';

const SERVICE_NAMES: Record<ServiceName, string> = {
  auth: 'Autenticacao',
  user: 'Usuarios',
  registration: 'Cadastros',
  inspection: 'Inspecoes',
  checklist: 'Checklists',
  notification: 'Notificacoes',
  scraper: 'Busca de Imoveis',
};

interface ServiceCheckProps {
  services: ServiceName[];
  children: ReactNode;
  fallback?: ReactNode;
  featureName?: string;
  showAlert?: boolean;
  onRetry?: () => void;
  loadingMessage?: string;
  skeleton?: ReactNode;
}

export function ServiceCheck({
  services,
  children,
  fallback,
  featureName,
  showAlert = false,
  onRetry,
  loadingMessage = 'Verificando disponibilidade dos servicos...',
  skeleton,
}: ServiceCheckProps): JSX.Element {
  // Usar o contexto diretamente em vez de useServiceCheck
  const { status, checkAllServices } = useServiceStatus();

  // Calcular estados baseado no status atual
  const offlineServices = services.filter(service => status[service] === 'offline');
  const anyOffline = offlineServices.length > 0;
  const isChecking = services.some(service => status[service] === 'checking');

  // Handler para retry
  const handleRetry = async () => {
    await checkAllServices();
    if (onRetry) {
      onRetry();
    }
  };

  // Mostra loading apenas se ainda estiver verificando
  if (isChecking) {
    return (
      <div className="flex justify-center items-center p-12">
        <Spin tip={loadingMessage} />
      </div>
    );
  }

  // Se algum servico estiver offline
  if (anyOffline) {
    // Modo alerta: mostra alerta + conteudo
    if (showAlert) {
      const offlineServiceNames = offlineServices.map(s => SERVICE_NAMES[s]).join(', ');
      return (
        <>
          <Alert
            type="warning"
            message="Algumas funcionalidades podem estar limitadas"
            description={`Os servicos de ${offlineServiceNames} estao temporariamente indisponiveis.`}
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
          {children}
        </>
      );
    }

    // Se tiver fallback customizado, usa ele
    if (fallback) {
      return <>{fallback}</>;
    }

    // Fallback padrao: componente ServiceUnavailable
    return (
      <ServiceUnavailable
        services={offlineServices}
        featureName={featureName}
        onRetry={handleRetry}
        showSkeleton={false}
        skeleton={skeleton}
      />
    );
  }

  // Tudo online, renderiza children
  return <>{children}</>;
}

export default ServiceCheck;
